// packages/client/src/components/project/miller/FolderColumn.tsx
import { ChevronRight, Folder as FolderIcon } from 'lucide-react';
import type { Folder } from '@estimator/shared';

interface FolderColumnProps {
  folders: Folder[];
  unfiledCount: number;
  selectedFolderId: string | null;
  onSelectFolder: (folder: Folder | null) => void;
  /** Override the column header label. Defaults to "Folders". */
  columnLabel?: string;
}

export default function FolderColumn({
  folders,
  unfiledCount,
  selectedFolderId,
  onSelectFolder,
  columnLabel = 'Folders',
}: FolderColumnProps) {
  return (
    <div
      className="flex flex-col h-full border-r"
      style={{ borderColor: 'var(--border)', minWidth: 220 }}
    >
      <div className="px-3 py-2 border-b" style={{ borderColor: 'var(--border)' }}>
        <h3 className="text-xs font-semibold uppercase tracking-wide" style={{ color: 'var(--text-muted)' }}>
          {columnLabel}
        </h3>
      </div>

      <div className="flex-1 overflow-y-auto">
        {folders.map((folder) => {
          const hasChildren = (folder.children_count ?? 0) > 0;
          const isSelected = selectedFolderId === folder.id;
          return (
            <button
              key={folder.id}
              onClick={() => onSelectFolder(folder)}
              className="w-full flex items-center gap-2 px-3 py-2 text-left text-sm transition-colors"
              style={{
                backgroundColor: isSelected ? 'var(--bg-hover)' : 'transparent',
                color: isSelected ? 'var(--accent)' : 'var(--text-primary)',
              }}
            >
              <FolderIcon size={16} style={{ color: 'var(--blue)', flexShrink: 0 }} />
              <span className="flex-1 truncate">{folder.name}</span>
              {hasChildren ? (
                <ChevronRight size={14} style={{ color: 'var(--text-muted)', flexShrink: 0 }} />
              ) : (
                (folder.item_count ?? 0) > 0 && (
                  <span className="text-[10px] font-ui" style={{ color: 'var(--text-muted)' }}>
                    {folder.item_count}
                  </span>
                )
              )}
            </button>
          );
        })}

        {unfiledCount > 0 && (
          <button
            onClick={() => onSelectFolder(null)}
            className="w-full flex items-center gap-2 px-3 py-2 text-left text-sm transition-colors"
            style={{
              backgroundColor: selectedFolderId === '__unfiled__' ? 'var(--bg-hover)' : 'transparent',
              color: 'var(--text-muted)',
            }}
          >
            <FolderIcon size={16} style={{ color: 'var(--text-muted)', flexShrink: 0 }} />
            <span className="flex-1 truncate">Unfiled</span>
            <span className="text-[10px] font-ui">{unfiledCount}</span>
          </button>
        )}
      </div>
    </div>
  );
}
